import type { FlyerIcon } from '@avrahami/contracts';

/**
 * The flyer's icons as SVG path data on a 24 by 24 grid, drawn as 2-unit strokes with round ends
 * (`drawIcon`). Simple line drawings, so they read at any size and in any of a store's colours.
 */
const CIRCLE = 'M21 12a9 9 0 1 1-18 0a9 9 0 1 1 18 0';

export const FLYER_ICON_PATHS: Readonly<Record<FlyerIcon, readonly string[]>> = {
  SPEED: [CIRCLE, 'M12 7v5l3.5 2'],
  CLEAN: [
    'M11 3l1.8 4.7L17.5 9.5l-4.7 1.8L11 16l-1.8-4.7L4.5 9.5l4.7-1.8z',
    'M18.5 14.5l.8 2.2l2.2.8l-2.2.8l-.8 2.2l-.8-2.2l-2.2-.8l2.2-.8z',
    'M5 17v4M3 19h4',
  ],
  SHIELD: ['M12 3l7 3v5c0 4.5-3 8.3-7 10c-4-1.7-7-5.5-7-10V6z', 'M8.8 12l2.2 2.2l4.2-4.4'],
  SAVINGS: [
    'M4 7c0-1.7 3.6-3 8-3s8 1.3 8 3s-3.6 3-8 3s-8-1.3-8-3z',
    'M4 7v5c0 1.7 3.6 3 8 3s8-1.3 8-3V7',
    'M4 12v5c0 1.7 3.6 3 8 3s8-1.3 8-3v-5',
  ],
  QUALITY: ['M12 3l2.6 5.6l6.1.7l-4.5 4.2l1.2 6L12 16.5l-5.4 3l1.2-6L3.3 9.3l6.1-.7z'],
  CAPACITY: ['M12 3l8 4.5v9L12 21l-8-4.5v-9z', 'M4 7.5l8 4.5l8-4.5', 'M12 12v9'],
  VARIETY: ['M4 4h6v6H4z', 'M14 4h6v6h-6z', 'M4 14h6v6H4z', 'M14 14h6v6h-6z'],
  POWER: ['M13 2L4 14h7l-1 8l9-12h-7z'],
  COLD: ['M12 2v20', 'M3.3 7l17.4 10', 'M3.3 17L20.7 7', 'M9 3.5l3 2l3-2', 'M9 20.5l3-2l3 2'],
  HEAT: [
    'M12 22c3.9 0 7-2.8 7-6.6c0-3.4-2.3-5.6-3.8-8.2c-.6 1.9-1.7 3-2.9 3.5c.3-3.2-.9-6.1-3.3-8.7c0 4.2-5 7-5 13.4c0 3.8 3.4 6.6 8 6.6z',
  ],
  PRECISION: [CIRCLE, 'M17 12a5 5 0 1 1-10 0a5 5 0 1 1 10 0', 'M13 12a1 1 0 1 1-2 0a1 1 0 1 1 2 0'],
  EASY: [
    'M7 10v11H3V10z',
    'M7 10l4-7c1.5 0 2.6 1.3 2.3 2.8L12.6 9H19a2 2 0 0 1 2 2.3l-1.4 8A2 2 0 0 1 17.6 21H7',
  ],
  SPACE: ['M8 3H3v5', 'M16 3h5v5', 'M8 21H3v-5', 'M16 21h5v-5', 'M9 9h6v6H9z'],
  SERVICE: [
    'M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.8-3.8a6 6 0 0 1-7.9 7.9l-6.9 6.9a2.1 2.1 0 0 1-3-3l6.9-6.9a6 6 0 0 1 7.9-7.9z',
  ],
};

/** The marks of a quote request and the store's contacts. */
export const CONTACT_PATHS: Readonly<Record<'QUOTE' | 'WHATSAPP' | 'PHONE', readonly string[]>> = {
  QUOTE: ['M6 3h9l4 4v14H6z', 'M15 3v4h4', 'M9.5 13.5l2 2l3.5-4'],
  WHATSAPP: [
    'M12 3a9 9 0 0 0-7.7 13.6L3 21l4.5-1.2A9 9 0 1 0 12 3z',
    'M9 8.5c-.8.6-1 1.8-.3 3.2c.8 1.6 2 2.8 3.6 3.6c1.4.7 2.6.5 3.2-.3l-1.8-1.6l-1.2.7a5 5 0 0 1-2-2l.7-1.2z',
  ],
  PHONE: [
    'M5 3.5h3.5l2 5l-2.5 1.6a11 11 0 0 0 5.9 5.9l1.6-2.5l5 2v3.5a2 2 0 0 1-2 2A17 17 0 0 1 3 5.5a2 2 0 0 1 2-2z',
  ],
};

/** The mark of the store's address. */
export const GLOBE_PATHS = [
  CIRCLE,
  'M3 12h18',
  'M12 3c2.5 2.7 3.8 5.7 3.8 9s-1.3 6.3-3.8 9c-2.5-2.7-3.8-5.7-3.8-9s1.3-6.3 3.8-9z',
];
